import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, FileCheck2, Pencil, Save as SaveIcon, X } from "lucide-react";
import { api, getErrorMessage } from "@/lib/api";
import { toast } from "sonner";

/**
 * Bibliothèque org-wide des types de documents requis pour les sous-traitants.
 * (Phase 175) — affiché dans Paramètres > Configuration de l'application
 */
export default function SubcontractorDocTypesCard({ canEdit = true }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState({});

  const fetchTypes = async () => {
    try {
      const { data } = await api.getSubcontractorDocTypes();
      setTypes(data || []);
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de chargement"));
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchTypes(); }, []);

  const handleCreate = async () => {
    const n = newName.trim();
    if (!n) return;
    try {
      await api.createSubcontractorDocType({ name: n, default_required: true, order: (types.at(-1)?.order ?? 0) + 1 });
      setNewName("");
      fetchTypes();
      toast.success("Type ajouté");
    } catch (e) { toast.error(getErrorMessage(e, "Erreur")); }
  };

  const handleSave = async (id) => {
    const draft = editing[id];
    if (!draft) return;
    try {
      await api.updateSubcontractorDocType(id, { name: draft.name?.trim(), default_required: draft.default_required });
      setEditing((s) => ({ ...s, [id]: undefined }));
      fetchTypes();
    } catch (e) { toast.error(getErrorMessage(e, "Erreur")); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Supprimer "${name}" ? Tous les documents de ce type associés à des sous-traitants seront aussi supprimés.`)) return;
    try {
      await api.deleteSubcontractorDocType(id);
      fetchTypes();
      toast.success("Type supprimé");
    } catch (e) { toast.error(getErrorMessage(e, "Erreur")); }
  };

  return (
    <Card data-testid="subcontractor-doc-types-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-heading flex items-center gap-2">
          <FileCheck2 className="h-5 w-5 text-primary" />
          Documents requis pour les sous-traitants
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Bibliothèque centrale de l'organisation : KBIS, attestations, etc. Vous cocherez ensuite ceux qui s'appliquent à chaque sous-traitant sur sa fiche.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <p className="text-sm text-muted-foreground">Chargement…</p>
        ) : (
          <>
            {types.length === 0 && (
              <p className="text-sm text-muted-foreground italic">
                Aucun type de document configuré. Commencez par exemple par "KBIS", "Attestation Décennale", "Attestation Vigilance URSSAF"…
              </p>
            )}
            <div className="space-y-2">
              {types.map((t) => {
                const draft = editing[t.id];
                if (draft) {
                  return (
                    <div key={t.id} className="flex items-center gap-2 p-2 border rounded-md bg-zinc-50">
                      <Input
                        value={draft.name}
                        onChange={(e) => setEditing((s) => ({ ...s, [t.id]: { ...draft, name: e.target.value } }))}
                        className="h-8 flex-1"
                        data-testid={`doc-type-edit-name-${t.id}`}
                      />
                      <div className="flex items-center gap-1.5">
                        <Switch
                          checked={!!draft.default_required}
                          onCheckedChange={(v) => setEditing((s) => ({ ...s, [t.id]: { ...draft, default_required: v } }))}
                        />
                        <Label className="text-xs">Requis par défaut</Label>
                      </div>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleSave(t.id)} data-testid={`doc-type-save-${t.id}`}>
                        <SaveIcon className="h-3.5 w-3.5 text-emerald-600" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEditing((s) => ({ ...s, [t.id]: undefined }))}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  );
                }
                return (
                  <div key={t.id} className="flex items-center gap-2 p-2 border rounded-md" data-testid={`doc-type-row-${t.id}`}>
                    <FileCheck2 className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm font-medium flex-1">{t.name}</span>
                    {t.default_required && (
                      <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">requis par défaut</span>
                    )}
                    {canEdit && (
                      <>
                        <Button
                          size="icon" variant="ghost" className="h-7 w-7"
                          onClick={() => setEditing((s) => ({ ...s, [t.id]: { name: t.name, default_required: !!t.default_required } }))}
                          data-testid={`doc-type-edit-${t.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon" variant="ghost" className="h-7 w-7"
                          onClick={() => handleDelete(t.id, t.name)}
                          data-testid={`doc-type-delete-${t.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            {canEdit && (
              <div className="flex items-center gap-2 pt-2 border-t">
                <Input
                  placeholder="Ex: KBIS, Attestation Décennale, Vigilance URSSAF…"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
                  className="h-8 flex-1"
                  data-testid="doc-type-new-name"
                />
                <Button size="sm" onClick={handleCreate} disabled={!newName.trim()} data-testid="doc-type-add-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
